import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { EnhancedButtonStatus } from '@components/button/EnhancedButton';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { drawerSections } from './content';
import { useLazyQuery, useMutation } from '@apollo/client';
import { cloneDeep, isEmpty } from 'lodash';
import { DEFAULT_ERROR_TEXT } from '@utils';
import { ITaxationDrawerInfo, ITaxationDrawerProps } from '.';
import {
  getListForm,
  createTaxation,
  updateTaxation,
  getLatestTaxationByLine,
  getTaxationDetailsInfo,
} from './queries';
import {
  extractLatestTaxationByLine,
  graphqlToTaxationInfo,
  toLookups,
} from './utils';
import { getError } from '@utils/graph-utils';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import { trimTrailingZeros } from '@utils/formatting-utils';
import PrecisionService from '@services/PrecisionService';
import DateService from '@services/DateService';
import DynamicFormDrawer from '@components/dynamic-form/DynamicFormDrawer';
import { IDynamicFormWidgetSection } from '@components/dynamic-form/types';
import { ISelectFormFieldProps } from '@components/dynamic-form/types/inputTypes';
import { normalizeDynamicFormDrawerValues } from '@components/dynamic-form/utils';
import { IAbstractRecord } from '@models';

const TaxationDrawer: React.FC<ITaxationDrawerProps> = ({
  open,
  onSuccess,
  onClose,
  taxationId,
}) => {
  const [booted, setBooted] = useState<boolean>(false);
  const [formDisabled, setFormDisabled] = useState(false);
  const [submitButtonState, setSubmitButtonState] =
    useState<EnhancedButtonStatus>();

  const [values, setValues] = useState<ITaxationDrawerInfo>();
  const [inputsForm, setInputsForm] =
    useState<IDynamicFormWidgetSection[]>(drawerSections);

  const [taxationEnumQuery] = useLazyQuery(getListForm(), {
    fetchPolicy: 'no-cache',
  });

  const [taxationInfoQuery] = useLazyQuery(getTaxationDetailsInfo(), {
    fetchPolicy: 'no-cache',
  });

  const loadLovList = async () => {
    const result = await taxationEnumQuery();
    const taxationEnums = toLookups(result.data);

    return taxationEnums;
  };

  const getEntityInfo = async () => {
    if (taxationId) {
      const apiResult = await taxationInfoQuery({
        variables: { id: taxationId },
      });
      if (apiResult.data) {
        const taxationEntity = graphqlToTaxationInfo(apiResult.data);
        return taxationEntity;
      }
    }

    return null;
  };

  const [getLatestTaxationByLineDetailsLazy] = useLazyQuery(
    getLatestTaxationByLine()
  );
  const [taxationAction] = useMutation(
    taxationId ? updateTaxation() : createTaxation()
  );

  const getLatestTaxationByLineDetails = async (selectedLineID: string) => {
    if (!isEmpty(selectedLineID)) {
      try {
        const response = await getLatestTaxationByLineDetailsLazy({
          variables: {
            selectedLineID: selectedLineID,
          },
        });

        const latestTaxationInfo = extractLatestTaxationByLine(response?.data);

        setInputsForm((currentInputsForm) => {
          const updatedSections = cloneDeep(currentInputsForm);
          const updatedInputs = updatedSections[0].inputs;

          updatedInputs.fixedStamp.value = latestTaxationInfo.fixedStamp;
          updatedInputs.fixedStampCurrency.value =
            latestTaxationInfo.fixedStampCurrency;
          updatedInputs.proportionalStamp.value =
            latestTaxationInfo.proportionalStamp
              ? latestTaxationInfo.proportionalStamp
              : '39735';
          updatedInputs.municipalityTax.value =
            latestTaxationInfo.municipalityTax;
          updatedInputs.taxOnCommission.value =
            latestTaxationInfo.taxOnCommission;
          updatedInputs.reinsuranceTax.value =
            latestTaxationInfo.reinsuranceTax;

          return updatedSections;
        });
      } catch (error) {
        console.error('Error fetching taxation details:', error);
      }
    }
  };

  const handleChange = (
    fieldName: keyof ITaxationDrawerInfo,
    value: any,
    sectionIndex: number,
    values: ITaxationDrawerInfo
  ) => {
    const newValues = cloneDeep(values) as any;
    const newSections = cloneDeep(inputsForm);
    newValues[fieldName] = value;
    newSections[0].inputs[fieldName].value = value;

    if (fieldName === 'line' && !taxationId) {
      getLatestTaxationByLineDetails(value);
    }

    setValues(newValues);
    setInputsForm(newSections);
  };

  const initialize = async () => {
    try {
      const updatedSections = cloneDeep(inputsForm);
      const updatedInputs = updatedSections[0].inputs;

      const [taxationEntity, lovData] = await Promise.all([
        getEntityInfo(),
        loadLovList(),
      ]);

      updatedInputs.line.disabled = !!taxationId;
      updatedInputs.effectiveTo.hidden = !taxationId;

      (updatedInputs.line as ISelectFormFieldProps).selectOptions =
        lovData['line'];

      if (taxationEntity) {
        updatedInputs.line.value = taxationEntity.line;
        updatedInputs.fixedStamp.value = trimTrailingZeros(
          taxationEntity.fixedStamp
        );
        updatedInputs.fixedStampCurrency.value =
          taxationEntity.fixedStampCurrency;
        updatedInputs.proportionalStamp.value =
          taxationEntity.proportionalStamp;
        updatedInputs.municipalityTax.value = taxationEntity.municipalityTax;
        updatedInputs.taxOnCommission.value = taxationEntity.taxOnCommission;
        updatedInputs.reinsuranceTax.value = taxationEntity.reinsuranceTax;
        updatedInputs.effectiveFrom.value = taxationEntity.effectiveFrom;
        updatedInputs.effectiveTo.value = taxationEntity.effectiveTo;
      }
      setBooted(true);
      setInputsForm(updatedSections);
    } catch (err) {
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    }
  };

  useEffect(() => {
    initialize();
  }, []);

  const submitForm = async (values: Record<string, any>) => {
    const [data] = normalizeDynamicFormDrawerValues(drawerSections, values);

    setFormDisabled(true);
    setSubmitButtonState('loading');

    try {
      let variablesMutation = {};
      if (!taxationId) {
        variablesMutation = {
          effectiveFrom: DateService.formatDateBackend(data.effectiveFrom),
          fixedStamp: parseFloat(data.fixedStamp),
          fixedStampCurrency: data.fixedStampCurrency,
          lineID: data.line,
          municipalityTax: PrecisionService.divideBy100(data.municipalityTax),
          proportionalStamp: PrecisionService.divideBy100(
            data.proportionalStamp
          ),
          reinsuranceTax: PrecisionService.divideBy100(data.reinsuranceTax),
          taxOnCommission: PrecisionService.divideBy100(data.taxOnCommission),
        };
      } else {
        variablesMutation = {
          entityId: taxationId,
          effectiveFrom: DateService.formatDateBackend(data.effectiveFrom),
          fixedStamp: parseFloat(data.fixedStamp),
          fixedStampCurrency: data.fixedStampCurrency,
          municipalityTax: PrecisionService.divideBy100(data.municipalityTax),
          proportionalStamp: PrecisionService.divideBy100(
            data.proportionalStamp
          ),
          reinsuranceTax: PrecisionService.divideBy100(data.reinsuranceTax),
          taxOnCommission: PrecisionService.divideBy100(data.taxOnCommission),
        };
      }

      const res = await taxationAction({
        variables: taxationId
          ? {
              ...variablesMutation,
              entityId: taxationId,
            }
          : variablesMutation,
        errorPolicy: 'all',
      });

      if (isEmpty(res.errors)) {
        toast.success(
          <ToastSuccessMessage>
            {taxationId
              ? 'Taxation Record successfully updated.'
              : 'Taxation Record successfully created.'}
          </ToastSuccessMessage>
        );
        setTimeout(() => {
          setSubmitButtonState('success');
          onSuccess();
          onClose();
        }, 500);
      } else {
        setSubmitButtonState(undefined);
        toast.error(<ToastErrorMessage>{getError(res)}</ToastErrorMessage>);
      }
    } catch (error) {
      setSubmitButtonState(undefined);
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    } finally {
      setFormDisabled(false);
    }
  };

  return (
    <DynamicFormDrawer
      title={taxationId ? 'Modify Taxation Record' : 'New Taxation Record'}
      onClose={() => onClose()}
      isOpen={open}
      loading={!booted}
      sections={inputsForm}
      onSubmit={(values) => submitForm(values)}
      submitButtonState={submitButtonState}
      disableForm={formDisabled}
      onChange={handleChange}
    />
  );
};

export default TaxationDrawer;
